import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePageMeta } from "../../lib/seo";
import { Phone, MessageCircle, MapPin, Shield, Users, Award, CheckCircle } from "lucide-react";
import Header from "./Header";
import { scrollToSection } from "../../lib/scrollToSection";
import Footer from "./Footer";
import MobilePhoneButton from "./MobilePhoneButton";
import CookieConsent from "./CookieConsent";
import { navData } from "../../data/mockData";

const AboutPage = () => {
  const navigate = useNavigate();

  usePageMeta({
    title: "About NeatWave | Cleaners in Cardiff, Barry & Penarth",
    description: "About NeatWave Cleaning Services: Cardiff-based domestic and commercial cleaners serving Cardiff, Barry and Penarth. DBS-checked, fully insured team. Call 0749 286 5011.",
  });

  const goToQuote = () => {
    navigate("/#quote-section");
    scrollToSection("quote-section");
  };

  return (
    <div className="min-h-screen bg-white font-inter">
      <Header scrollToQuote={goToQuote} />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-sky-50 to-white pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            About NeatWave Cleaning Services
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Domestic and commercial cleaning across Cardiff, Barry and Penarth. Your reliable partner for spotless homes and workplaces.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Our Story</h2>
          <div className="prose prose-lg max-w-none text-slate-600">
            <p className="mb-4">
              NeatWave Cleaning Services was founded with a simple mission: to provide professional, reliable cleaning that gives people back their time. We understand how valuable your time is, and we're passionate about delivering cleaning services you can trust.
            </p>
            <p className="mb-4">
              Based in Cardiff and serving Cardiff, Barry and Penarth, we've built our reputation on consistency, quality, and genuine care for our clients' homes and businesses. Every member of our team shares our commitment to excellence and customer satisfaction.
            </p>
            <p className="mb-4">
              From regular domestic cleaning to specialist services like end of tenancy and Airbnb turnovers, we serve homeowners, landlords, letting agents, Airbnb hosts and businesses throughout Cardiff, Barry and Penarth. Our growth comes from word-of-mouth recommendations - the best testament to the quality of our work.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Why Choose NeatWave?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <Shield className="w-12 h-12 text-sky-600 mb-4" />
              <h3 className="font-bold text-slate-900 mb-2">Fully Insured & DBS Checked</h3>
              <p className="text-slate-600">All our cleaners are DBS-checked and we carry full public liability insurance for your complete peace of mind.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <Users className="w-12 h-12 text-sky-600 mb-4" />
              <h3 className="font-bold text-slate-900 mb-2">Experienced Team</h3>
              <p className="text-slate-600">Professional cleaners trained to a consistent, checklist-based standard for domestic and commercial cleaning.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <Award className="w-12 h-12 text-sky-600 mb-4" />
              <h3 className="font-bold text-slate-900 mb-2">48-Hour Re-clean Policy</h3>
              <p className="text-slate-600">If we've missed something in the booked areas, tell us within 48 hours and we'll come back to put it right.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <CheckCircle className="w-12 h-12 text-sky-600 mb-4" />
              <h3 className="font-bold text-slate-900 mb-2">Eco-Friendly Products</h3>
              <p className="text-slate-600">We use professional-grade, eco-friendly cleaning products that are safe for your family, pets, and the environment.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <MapPin className="w-12 h-12 text-sky-600 mb-4" />
              <h3 className="font-bold text-slate-900 mb-2">Local & Reliable</h3>
              <p className="text-slate-600">Based in Cardiff, we're part of your community. Punctual, professional, and always respectful of your property.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <Phone className="w-12 h-12 text-sky-600 mb-4" />
              <h3 className="font-bold text-slate-900 mb-2">Responsive Support</h3>
              <p className="text-slate-600">Quick response times, flexible scheduling, and friendly customer service. We're here when you need us.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-6 text-center">Service Areas</h2>
          <p className="text-lg text-slate-600 text-center mb-8">
            We clean homes and businesses in Cardiff, Barry and Penarth. Our team travels to you from Cardiff; we don't have separate branches or offices in Barry or Penarth.
          </p>
          <div className="bg-sky-50 p-8 rounded-xl">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <h3 className="font-bold text-slate-900 mb-2"><Link to="/" className="hover:text-sky-600" data-testid="about-area-cardiff">Cardiff</Link></h3>
                <p className="text-sm text-slate-600">Our main area, including Canton, Cathays, Roath, Pontcanna, Whitchurch, Llandaff and Cardiff Bay</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-2"><Link to="/cleaning-services-barry" className="hover:text-sky-600" data-testid="about-area-barry">Barry</Link></h3>
                <p className="text-sm text-slate-600">Homes, holiday lets, rentals and businesses across Barry</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-2"><Link to="/cleaning-services-penarth" className="hover:text-sky-600" data-testid="about-area-penarth">Penarth</Link></h3>
                <p className="text-sm text-slate-600">Homes, flats, rentals and businesses across Penarth</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 text-center mt-6">
              Not sure if we cover your street? <a href={`tel:${navData.phoneLink}`} className="text-sky-600 font-semibold hover:underline">Call us</a> to check.
            </p>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-sky-600 to-sky-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Experience the NeatWave Difference?</h2>
          <p className="text-xl mb-8">Get your free, no-obligation quote today</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={goToQuote}
              className="inline-flex items-center bg-white text-sky-600 hover:bg-slate-100 font-semibold px-8 py-3 rounded-lg shadow-lg transition-colors"
            >
              Get Free Quote
            </button>
            <a
              href={`tel:${navData.phoneLink}`}
              className="inline-flex items-center bg-sky-800 hover:bg-sky-900 text-white font-semibold px-8 py-3 rounded-lg shadow-lg transition-colors"
            >
              <Phone className="w-5 h-5 mr-2" />
              {navData.phone}
            </a>
            <a
              href={navData.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-lg shadow-lg transition-colors"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <MobilePhoneButton phoneLink={navData.phoneLink} />
      <CookieConsent />
    </div>
  );
};

export default AboutPage;